import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../layout/MainLayout";
import {
    getPublicPsychInstrument,
    getPsychResult,
    listPsychInstruments,
    listPsychResults,
} from "../services/psychTestApi";
import {
    getPublicProfessionalResult,
    listPublicProfessionalResults,
} from "../services/publicProfessionalTestApi";
import { useAlertContext } from "../context/AlertContext";
import PsychResultReport from "../components/psych/PsychResultReport";
import ProfResultReport from "../components/professional-test/ProfResultReport";
import { IntegrityBadge } from "../components/professional-test/IntegrityPanel";
import { integrityVerdict } from "../utils/testIntegrity";
import TestResultAccordionItem from "../components/tests/TestResultAccordionItem";
import { getCandidate, getTestResults } from "../services/candidateApi";
import { getTest, getTests, getVacancyTestSettings, listExternalTestResults } from "../services/testApi";
import { getVacancyOptions } from "../services/vacancyApi";
import TestDetails from "../components/tests/TestDetails";
import CandidateDetails from "../components/candidates/CandidateDetails";
import { isArchiveStatus, statusLabel, statusTone } from "../utils/candidateStatuses";

// Правка №37: три типа — анкета, профессиональный, психологический; коротко.
function typeLabel(kind, universal = false) {
    if (kind === "psychological") return "Псих.";
    if (universal) return "Анкета";
    if (kind === "professional" || kind === "candidate_professional") return "Проф.";
    return "—";
}

function typeTitle(kind, universal = false) {
    if (kind === "psychological") return "Психологический тест";
    if (universal) return "Анкета кандидата";
    return "Профессиональный тест";
}

function professionalTestOrigin(testType) {
    const normalized = String(testType || "").trim().toLowerCase();
    if (normalized === "url") {
        return {
            label: "Внешний",
            className: "bg-violet-50 text-violet-700 border-violet-200",
        };
    }
    if (normalized === "вопросно-ответная форма") {
        // Правка №1: тест платформы — вариант по умолчанию, плашку не показываем.
        return null;
    }
    return null;
}

function profSummary(row) {
    if (row.score != null && row.max_score != null) {
        return `${row.score}/${row.max_score}`;
    }
    if (row.timed_out) return "Время вышло";
    return "Ответы";
}

function parseTestFilter(value) {
    const [kind, ...idParts] = String(value || "").split(":");
    const id = idParts.join(":");
    return kind && id ? { kind, id } : null;
}

function statusBadgeClass(status) {
    const tone = statusTone(status);
    if (tone === "red") return "bg-rose-50 text-rose-700 border-rose-200";
    if (tone === "yellow") return "bg-amber-50 text-amber-800 border-amber-200";
    if (tone === "green") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
}

function PsychInstrumentDetails({ instrument, onClose }) {
    const itemCount = instrument.item_count ?? instrument.items?.length;

    return (
        <div data-testid="psych-instrument-detail">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">
                        Психологический тест
                    </p>
                    <h2 className="text-2xl font-bold text-slate-900">{instrument.name}</h2>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-sm px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium text-slate-700"
                >
                    Закрыть
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
                {instrument.version && (
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">
                        Версия {instrument.version}
                    </span>
                )}
                {itemCount != null && (
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">
                        {itemCount} пунктов
                    </span>
                )}
                {instrument.duration_minutes && (
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">
                        Около {instrument.duration_minutes} мин.
                    </span>
                )}
            </div>

            {instrument.description && (
                <section className="mb-5">
                    <h3 className="font-semibold text-slate-900 mb-2">Описание</h3>
                    <p className="text-slate-700 bg-slate-50 p-4 rounded-xl whitespace-pre-wrap">
                        {instrument.description}
                    </p>
                </section>
            )}
            {instrument.instruction && (
                <section>
                    <h3 className="font-semibold text-slate-900 mb-2">Инструкция участнику</h3>
                    <p className="text-slate-700 bg-slate-50 p-4 rounded-xl whitespace-pre-wrap">
                        {instrument.instruction}
                    </p>
                </section>
            )}
        </div>
    );
}

export default function PsychResultsPage() {
    const { showAlert } = useAlertContext();
    const deepLink = useMemo(() => {
        const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        const candidate = Number(params.get("candidate_id"));
        const result = Number(params.get("result_id"));
        return {
            candidateId: Number.isInteger(candidate) && candidate > 0 ? candidate : null,
            resultId: Number.isInteger(result) && result > 0 ? result : null,
            source: params.get("source") || null,
            testId: Number(params.get("test_id")) > 0 ? Number(params.get("test_id")) : null,
            integrity: ["violations", "suspicious"].includes(params.get("integrity")) ? params.get("integrity") : "all",
            type: ["psychological", "professional", "all"].includes(params.get("type"))
                ? params.get("type")
                : "all",
        };
    }, []);
    const deepLinkOpened = useRef(false);
    const testRequestId = useRef(0);
    const resultRequestId = useRef(0);
    const candidateRequestId = useRef(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [viewedTest, setViewedTest] = useState(null);
    const [testLoading, setTestLoading] = useState(false);
    const [typeFilter, setTypeFilter] = useState(deepLink.type);
    const [fioQuery, setFioQuery] = useState("");
    const [testFilter, setTestFilter] = useState(deepLink.testId ? `professional:${deepLink.testId}` : "");
    const [appliedFio, setAppliedFio] = useState("");
    const [appliedTestFilter, setAppliedTestFilter] = useState(deepLink.testId ? `professional:${deepLink.testId}` : "");
    const [testOptions, setTestOptions] = useState([]);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [candidateLoading, setCandidateLoading] = useState(false);
    const [integrityTab, setIntegrityTab] = useState(deepLink.integrity);
    // Агент-баги 04.11.2026 (Света): фильтр по вакансии — тесты, подключённые к вакансии.
    const [vacancyOptions, setVacancyOptions] = useState([]);
    const [vacancyFilter, setVacancyFilter] = useState("");
    const [vacancyTestIds, setVacancyTestIds] = useState(null);
    useEffect(() => {
        let alive = true;
        getVacancyOptions().then((list) => { if (alive) setVacancyOptions(Array.isArray(list) ? list : []); })
            .catch(() => { if (alive) setVacancyOptions([]); });
        return () => { alive = false; };
    }, []);
    useEffect(() => {
        if (!vacancyFilter) { setVacancyTestIds(null); return undefined; }
        let alive = true;
        Promise.resolve(getVacancyTestSettings(vacancyFilter))
            .then((data) => {
                if (!alive) return;
                const ids = new Set([
                    ...((data && data.test_ids) || []),
                    ...(((data && data.items) || []).map((item) => item.test_id)),
                ].filter((id) => id != null).map(String));
                setVacancyTestIds(ids);
            })
            .catch(() => { if (alive) setVacancyTestIds(new Set()); });
        return () => { alive = false; };
    }, [vacancyFilter]);
    const vacancyItems = vacancyTestIds
        ? items.filter((row) => row._kind !== "psychological" && vacancyTestIds.has(String(row.test_id ?? row.test?.id)))
        : items;
    const violationCount = vacancyItems.filter((row) => integrityVerdict(row.integrity) === "violation").length;
    const suspiciousCount = vacancyItems.filter((row) => integrityVerdict(row.integrity) === "suspicious").length;
    const visibleItems = integrityTab === "all"
        ? vacancyItems
        : vacancyItems.filter((row) => integrityVerdict(row.integrity) === (integrityTab === "violations" ? "violation" : "suspicious"));

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const selectedTest = parseTestFilter(appliedTestFilter);
            const baseFilters = {
                candidate_id: deepLink.candidateId || undefined,
                q: appliedFio || undefined,
            };
            const wantPsych = (typeFilter === "all" || typeFilter === "psychological")
                && (!selectedTest || selectedTest.kind === "psychological");
            const wantProf = (typeFilter === "all" || typeFilter === "professional")
                && (!selectedTest || selectedTest.kind === "professional");
            const psychFilters = {
                ...baseFilters,
                instrument_id: selectedTest?.kind === "psychological" ? selectedTest.id : undefined,
            };
            const profFilters = {
                ...baseFilters,
                test_id: selectedTest?.kind === "professional" ? selectedTest.id : undefined,
            };

            const [psychRaw, profRaw, externalRaw, legacyRaw, psychCatalogRaw, profCatalogRaw] = await Promise.all([
                wantPsych ? listPsychResults(psychFilters) : Promise.resolve([]),
                wantProf ? listPublicProfessionalResults(profFilters) : Promise.resolve([]),
                wantProf ? listExternalTestResults(profFilters) : Promise.resolve([]),
                wantProf && deepLink.candidateId
                    ? getTestResults(deepLink.candidateId).catch(() => [])
                    : Promise.resolve([]),
                (typeFilter === "all" || typeFilter === "psychological")
                    ? listPsychInstruments().catch(() => [])
                    : Promise.resolve([]),
                (typeFilter === "all" || typeFilter === "professional")
                    ? getTests().catch(() => [])
                    : Promise.resolve([]),
            ]);

            const psychCatalog = Array.isArray(psychCatalogRaw)
                ? psychCatalogRaw
                : psychCatalogRaw?.items || [];
            const psychById = new Map(psychCatalog.map((item) => [String(item.id), item]));
            const profCatalog = Array.isArray(profCatalogRaw)
                ? profCatalogRaw
                : profCatalogRaw?.items || [];
            const profById = new Map(profCatalog.map((item) => [String(item.id), item]));
            setTestOptions([
                ...psychCatalog.map((item) => ({
                    value: `psychological:${item.id}`,
                    name: item.name || `Психологический тест ${item.id}`,
                    kind: "psychological",
                })),
                ...profCatalog.map((item) => ({
                    value: `professional:${item.id}`,
                    name: `${item.name || `Профессиональный тест ${item.id}`}${
                        String(item.test_type || "").toLowerCase() === "url"
                            ? " — внешняя ссылка"
                            : ""
                    }`,
                    kind: "professional",
                })),
            ].sort((a, b) => a.name.localeCompare(b.name, "ru")));

            const psychItems = (Array.isArray(psychRaw) ? psychRaw : psychRaw?.items || []).map(
                (row) => {
                    const instrument = psychById.get(String(row.instrument_id));
                    return {
                        ...row,
                        _kind: "psychological",
                        _key: `psych-${row.id}`,
                        _title: instrument?.name || "Психологический опросник",
                        _test: instrument || null,
                    };
                }
            );
            const profItems = (Array.isArray(profRaw) ? profRaw : profRaw?.items || []).map(
                (row) => ({
                    ...row,
                    _kind: "professional",
                    _universal: profById.get(String(row.test_id))?.test_category === "universal",
                    _key: `prof-${row.id}`,
                    _title: row.test_name || `Тест #${row.test_id}`,
                    _testType: row.test_type || profById.get(String(row.test_id))?.test_type || null,
                })
            );
            const publicCandidateResultIds = new Set(
                profItems.map((row) => Number(row.candidate_result_id)).filter(Boolean)
            );
            const externalItems = (Array.isArray(externalRaw) ? externalRaw : externalRaw?.items || [])
                .map((row) => ({
                    ...row,
                    _kind: "candidate_professional",
                    _key: `external-prof-${row.id}`,
                    _title: row.test_name || `Тест #${row.test_id}`,
                    _testType: row.test_type || "url",
                }));
            const externalResultIds = new Set(externalItems.map((row) => Number(row.id)));
            const legacyItems = (Array.isArray(legacyRaw) ? legacyRaw : legacyRaw?.items || [])
                .filter((row) => !publicCandidateResultIds.has(Number(row.id)))
                .filter((row) => !externalResultIds.has(Number(row.id)))
                .filter((row) => row.score != null || row.comment || row.has_image)
                .map((row) => ({
                    ...row,
                    candidate_id: row.candidate_id ?? deepLink.candidateId,
                    _kind: "candidate_professional",
                    _universal: profById.get(String(row.test_id))?.test_category === "universal",
                    _key: `candidate-prof-${row.id}`,
                    _title: row.test?.name || `Тест #${row.test_id}`,
                    _testType: row.test?.test_type || profById.get(String(row.test_id))?.test_type || null,
                    taken_at: row.updated_at || row.created_at,
                }));

            const merged = [...psychItems, ...profItems, ...externalItems, ...legacyItems].sort((a, b) => {
                const da = a.taken_at || a.created_at || "";
                const db = b.taken_at || b.created_at || "";
                return String(db).localeCompare(String(da));
            });
            setItems(merged);
        } catch (e) {
            showAlert(`Не удалось загрузить результаты: ${e.message || e}`, "error");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [showAlert, typeFilter, appliedFio, appliedTestFilter, deepLink.candidateId]);

    useEffect(() => {
        load();
    }, [load]);

    const applyFilters = (e) => {
        e?.preventDefault?.();
        setAppliedFio(fioQuery.trim());
        setAppliedTestFilter(testFilter);
    };

    const changeTypeFilter = (value) => {
        setTypeFilter(value);
        const selectedTest = parseTestFilter(testFilter);
        if (selectedTest && value !== "all" && selectedTest.kind !== value) {
            setTestFilter("");
            setAppliedTestFilter("");
        }
    };

    const openCandidate = useCallback(async (candidateId) => {
        const requestId = candidateRequestId.current + 1;
        candidateRequestId.current = requestId;
        setSelectedCandidate(null);
        setCandidateLoading(true);
        try {
            const candidate = await getCandidate(candidateId);
            if (candidateRequestId.current === requestId) setSelectedCandidate(candidate);
        } catch (e) {
            if (candidateRequestId.current === requestId) {
                showAlert(`Не удалось открыть карточку кандидата: ${e.message || e}`, "error");
            }
        } finally {
            if (candidateRequestId.current === requestId) setCandidateLoading(false);
        }
    }, [showAlert]);

    const closeCandidate = useCallback(() => {
        candidateRequestId.current += 1;
        setSelectedCandidate(null);
        setCandidateLoading(false);
    }, []);

    const openDetail = useCallback(async (row) => {
        const requestId = resultRequestId.current + 1;
        resultRequestId.current = requestId;
        try {
            testRequestId.current += 1;
            setViewedTest(null);
            setTestLoading(false);
            setSelected(null);
            setDetailLoading(true);
            if (row._kind === "candidate_professional") {
                if (resultRequestId.current === requestId) setSelected(row);
            } else if (row._kind === "professional") {
                const full = await getPublicProfessionalResult(row.id);
                if (resultRequestId.current === requestId) {
                    setSelected({ ...full, _kind: "professional" });
                }
            } else {
                const full = await getPsychResult(row.id);
                if (resultRequestId.current === requestId) {
                    setSelected({ ...full, _kind: "psychological" });
                }
            }
        } catch (e) {
            if (resultRequestId.current === requestId) {
                showAlert(`Не удалось открыть результат: ${e.message || e}`, "error");
            }
        } finally {
            if (resultRequestId.current === requestId) setDetailLoading(false);
        }
    }, [showAlert]);

    const closeResult = useCallback(() => {
        resultRequestId.current += 1;
        setSelected(null);
        setDetailLoading(false);
    }, []);

    const closeTest = useCallback(() => {
        testRequestId.current += 1;
        setViewedTest(null);
        setTestLoading(false);
    }, []);

    const openTest = useCallback(async (row) => {
        const requestId = testRequestId.current + 1;
        testRequestId.current = requestId;
        resultRequestId.current += 1;
        setSelected(null);
        setDetailLoading(false);
        setViewedTest(null);
        setTestLoading(true);

        try {
            if (row._kind === "psychological") {
                if (!row.instrument_id) {
                    throw new Error("Для результата не указан психологический тест");
                }
                const instrument = await getPublicPsychInstrument(row.instrument_id);
                if (!instrument) throw new Error("Психологический тест больше недоступен");
                if (testRequestId.current === requestId) {
                    setViewedTest({ kind: "psychological", data: instrument });
                }
                return;
            }

            const testId = Number(row.test_id ?? row.test?.id);
            if (!Number.isInteger(testId) || testId <= 0) {
                throw new Error("Для результата не указан профессиональный тест");
            }
            const test = await getTest(testId);
            if (!test) throw new Error("Тест был удалён или больше недоступен");
            if (testRequestId.current === requestId) {
                setViewedTest({ kind: "professional", data: test });
            }
        } catch (e) {
            if (testRequestId.current === requestId) {
                showAlert(`Не удалось открыть тест: ${e.message || e}`, "error");
            }
        } finally {
            if (testRequestId.current === requestId) setTestLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        if (deepLinkOpened.current || !deepLink.resultId || items.length === 0) return;
        const row = items.find((item) => (
            Number(item.id) === deepLink.resultId
            && (!deepLink.source || item._kind === deepLink.source || (
                deepLink.source === "public_professional" && item._kind === "professional"
            ))
        ));
        if (row) {
            deepLinkOpened.current = true;
            openDetail(row);
        }
    }, [items, deepLink.resultId, deepLink.source, openDetail]);

    return (
        <MainLayout className="flex flex-col h-full overflow-y-auto">
            <div className="mb-6 flex justify-between items-center gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900">Результаты тестов</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Психологические и профессиональные тесты{deepLink.candidateId ? " выбранного кандидата" : ""}.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="text-sm px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium disabled:opacity-50"
                >
                    {loading ? "Обновление…" : "Обновить"}
                </button>
            </div>

            <form
                onSubmit={applyFilters}
                className="mb-4 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4 grid gap-3 md:grid-cols-5"
                data-testid="test-results-filters"
            >
                <label className="block text-sm">
                    <span className="text-slate-500">Тип теста</span>
                    <select
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white"
                        value={typeFilter}
                        onChange={(e) => changeTypeFilter(e.target.value)}
                        data-testid="filter-test-type"
                    >
                        <option value="all">Все</option>
                        <option value="psychological">Психологический</option>
                        <option value="professional">Профессиональный</option>
                    </select>
                </label>
                <label className="block text-sm">
                    <span className="text-slate-500">Поиск по ФИО</span>
                    <input
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                        value={fioQuery}
                        onChange={(e) => setFioQuery(e.target.value)}
                        placeholder="Иванов"
                        data-testid="filter-fio"
                    />
                </label>
                <label className="block text-sm">
                    <span className="text-slate-500">Тестирование</span>
                    <select
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white"
                        value={testFilter}
                        onChange={(e) => setTestFilter(e.target.value)}
                        data-testid="filter-test"
                    >
                        <option value="">Все тестирования</option>
                        {testOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block text-sm">
                    <span className="text-slate-500">Вакансия</span>
                    <select
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white"
                        value={vacancyFilter}
                        onChange={(e) => setVacancyFilter(e.target.value)}
                        data-testid="filter-vacancy"
                    >
                        <option value="">Все вакансии</option>
                        {vacancyOptions.map((v) => (
                            <option key={v.id} value={String(v.id)}>{v.name || `Вакансия #${v.id}`}</option>
                        ))}
                    </select>
                </label>
                <div className="flex items-end">
                    <button
                        type="submit"
                        className="w-full text-sm px-4 py-2.5 rounded-xl bg-slate-900 text-white font-medium hover:bg-slate-800"
                        data-testid="filter-apply"
                    >
                        Применить
                    </button>
                </div>
            </form>

            <div className="mb-3 flex flex-wrap gap-2" data-testid="integrity-tabs">
                {[
                    ["all", "Все результаты", vacancyItems.length],
                    ["violations", "Нарушения", violationCount],
                    ["suspicious", "Подозрительные", suspiciousCount],
                ].map(([key, label, count]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setIntegrityTab(key)}
                        data-testid={`integrity-tab-${key}`}
                        className={`rounded-xl border px-4 py-2 text-sm font-semibold ${integrityTab === key ? "border-slate-900 bg-slate-900 text-white" : key === "violations" && count ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                    >
                        {label} ({count})
                    </button>
                ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {loading && items.length === 0 ? (
                    <p className="p-8 text-slate-500 text-sm">Загрузка…</p>
                ) : items.length === 0 ? (
                    <p className="p-8 text-slate-500 text-sm">Результатов пока нет.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="psych-results-table">
                            <thead className="bg-slate-50 text-slate-500 text-left">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Дата</th>
                                    <th className="px-4 py-3 font-medium">Тип</th>
                                    <th className="px-4 py-3 font-medium">Тест</th>
                                    <th className="px-4 py-3 font-medium">ФИО</th>
                                    <th className="px-4 py-3 font-medium">Статус кандидата</th>
                                    <th className="px-4 py-3 font-medium">Специальность</th>
                                    <th className="px-4 py-3 font-medium">Сводка</th>
                                    <th className="px-4 py-3 font-medium">Честность</th>
                                    <th className="px-4 py-3 font-medium">ЧС / ЧМ</th>
                                    <th className="px-4 py-3 font-medium" />
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((row) => {
                                    const origin = row._kind === "psychological"
                                        ? null
                                        : professionalTestOrigin(row._testType);
                                    return (
                                    <tr key={row._key} className="border-t border-slate-100 hover:bg-slate-50/50">
                                        <td className="px-4 py-3 text-slate-600">
                                            {row.taken_at
                                                ? new Date(row.taken_at).toLocaleDateString("ru-RU")
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col items-start gap-1">
                                                <span
                                                    title={typeTitle(row._kind, row._universal)}
                                                    className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-semibold ${
                                                        row._kind === "psychological"
                                                            ? "bg-amber-50 text-amber-800 border-amber-100"
                                                            : row._universal
                                                                ? "bg-emerald-50 text-emerald-800 border-emerald-100"
                                                                : "bg-blue-50 text-blue-800 border-blue-100"
                                                    }`}
                                                >
                                                    {typeLabel(row._kind, row._universal)}
                                                </span>
                                                {origin && (
                                                    <span
                                                        className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${origin.className}`}
                                                        data-testid={`test-origin-${row._key}`}
                                                    >
                                                        {origin.label}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 max-w-[220px]" title={row._title}>
                                            <button
                                                type="button"
                                                onClick={() => openTest(row)}
                                                className="block max-w-full truncate text-left font-medium text-[#8f7418] hover:text-[#6f590f] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                                                aria-label={`Открыть тест: ${row._title}`}
                                            >
                                                {row._title}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-left font-medium text-slate-900">
                                            {Number(row.candidate_id) > 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openCandidate(row.candidate_id)}
                                                    className="rounded text-left text-[#8f7418] hover:text-[#6f590f] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                                                    aria-label={`Открыть карточку кандидата: ${row.full_name}`}
                                                >
                                                    {row.full_name}
                                                </button>
                                            ) : row.full_name}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.candidate_status ? (
                                                <span
                                                    className={`inline-flex max-w-[220px] px-2.5 py-1 rounded-full border text-xs font-semibold leading-4 ${statusBadgeClass(row.candidate_status)}`}
                                                    title={statusLabel(row.candidate_status, row.candidate_status_date)}
                                                >
                                                    {statusLabel(row.candidate_status, row.candidate_status_date)}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">Не указан</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{row.position || "—"}</td>
                                        <td className="px-4 py-3 text-slate-600">
                                            {row._kind === "psychological"
                                                ? (
                                                    <span className={
                                                        String(row.quality_status || "").includes("Критич")
                                                            ? "text-rose-700 font-semibold"
                                                            : ""
                                                    }>
                                                        {row.quality_status || "Результат готов"}
                                                    </span>
                                                )
                                                : profSummary(row)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row._kind === "professional"
                                                ? <IntegrityBadge integrity={row.integrity} testId={`integrity-${row._key}`} />
                                                : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 tabular-nums">
                                            {row._kind === "psychological"
                                                ? (row.chs != null || row.chm != null
                                                    ? `ЧС ${row.chs ?? "—"} · ЧМ ${row.chm ?? "—"}`
                                                    : "—")
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                type="button"
                                                onClick={() => openDetail(row)}
                                                className="text-sm font-medium text-[#a88b20] hover:underline"
                                            >
                                                {row._kind === "psychological" ? "Подробнее" : "Открыть"}
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {(selectedCandidate || candidateLoading) && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start z-50 p-4 overflow-y-auto"
                    onClick={closeCandidate}
                    data-testid="candidate-card-modal"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-[1800px] h-[96vh] flex flex-col overflow-hidden ring-1 ring-slate-900/5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 shrink-0">
                            <h2 className="text-lg font-bold text-slate-900">Карточка кандидата</h2>
                            <button
                                type="button"
                                onClick={closeCandidate}
                                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
                                aria-label="Закрыть карточку кандидата"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-5 sm:p-6 overflow-y-auto flex-1">
                            {candidateLoading && !selectedCandidate ? (
                                <p className="text-slate-500">Загрузка карточки…</p>
                            ) : (
                                <CandidateDetails
                                    candidate={selectedCandidate}
                                    isArchiveView={isArchiveStatus(selectedCandidate?.status)}
                                    onChange={load}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {(selected || detailLoading) && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start z-50 p-4 overflow-y-auto"
                    onClick={closeResult}
                >
                    {detailLoading && !selected ? (
                        <div
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 mt-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <p className="text-slate-500">Загрузка…</p>
                        </div>
                    ) : selected?._kind === "psychological" ? (
                        <div className="w-full max-w-[1368px] my-4" onClick={(e) => e.stopPropagation()}>
                            <PsychResultReport result={selected} onClose={closeResult} />
                        </div>
                    ) : selected?._kind === "candidate_professional" ? (
                        <div
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 my-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center gap-4 mb-4">
                                <h2 className="text-xl font-bold text-slate-900">{selected._title}</h2>
                                <button type="button" onClick={closeResult} className="text-slate-500 hover:text-slate-900">Закрыть</button>
                            </div>
                            <TestResultAccordionItem result={selected} defaultOpen />
                        </div>
                    ) : (
                        <ProfResultReport result={selected} onClose={closeResult} />
                    )}
                </div>
            )}

            {(viewedTest || testLoading) && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
                    onClick={closeTest}
                    data-testid="test-detail-modal"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto ring-1 ring-slate-900/5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {testLoading && !viewedTest ? (
                                <p className="text-slate-500">Загрузка теста…</p>
                            ) : viewedTest?.kind === "psychological" ? (
                                <PsychInstrumentDetails instrument={viewedTest.data} onClose={closeTest} />
                            ) : viewedTest ? (
                                <TestDetails test={viewedTest.data} onClose={closeTest} />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
}
